/* eslint-disable camelcase */
import { useLocation } from 'react-router-dom'
import { trackInternalEvent } from '@rio/tracking'
import { Fragment } from 'react'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import ModalOverlay from 'builder/components/ModalOverlay'
import { ButtonTheme } from 'builder/components/Button'
import { navigate } from 'builder/modules/navigate'
import {
  Container,
  Title,
  CloseButton,
  Modal,
  ItemsTitle,
  ItemContainer,
  TickContainer,
  Tick,
  ItemDescriptionContainer,
  ItemTitle,
  ItemDescription,
  Footer,
  Body,
  Items,
  CTA,
} from './styles'
import { useAppWelcomeModal } from './useAppWelcomeModal'

const documentRoutes = ['/', '/resumes', '/tr-documents']
const jobSearchRoute = '/job-search'

export const AppWelcomeModal = () => {
  const { i18n } = useI18n()
  const { pathname } = useLocation()
  const { isPhone } = useMediaQueries()
  const { showWelcomeModal, documents, closeModal } = useAppWelcomeModal()
  const { hasResumeByExpert, refreshedResume, hasResumeDistribution, resumeByExpert } = documents

  /**
   * Determines which CTA to show on the WelcomeModal based on user state and route.
   *
   * Case 0:
   * - User is on ['/', '/resumes', '/tr-documents']
   * - No professionally written resume
   * - No refreshed resume
   *
   * Case 1:
   * - User is on ['/', '/resumes', '/tr-documents']
   * - Has a professionally written resume
   * - Has access to resume distribution
   *
   * Case 2:
   * - User is on ['/', '/resumes', '/tr-documents']
   * - Has a professionally written resume
   * - Does NOT have access to resume distribution
   *
   * Case 3:
   * - User is on ['/', '/resumes', '/tr-documents']
   * - No professionally written resume
   * - Has a refreshed resume
   *
   * Case 4:
   * - User is on the /job-search page
   */
  const showCase0 = !hasResumeByExpert && !refreshedResume && documentRoutes.includes(pathname)
  const showCase1 = hasResumeByExpert && hasResumeDistribution && documentRoutes.includes(pathname)
  const showCase2 = hasResumeByExpert && !hasResumeDistribution && documentRoutes.includes(pathname)
  const showCase3 = !hasResumeByExpert && refreshedResume && documentRoutes.includes(pathname)
  const showCase4 = pathname === jobSearchRoute

  const closeModalWithRedirect = (): void => {
    closeModal()

    if (hasResumeByExpert) {
      /**
       * Here we stimulate a browser refresh to redirect the user to the /tr-documents page.
       * This is needed because the tabs component on the DocumentsSection component is not
       * re-rendering to route navigation changes.
       */
      window.location.replace(`/app/tr-documents`)
    }
  }

  if (!showWelcomeModal) return null

  return (
    <ModalOverlay
      overlayFadeDuration={300}
      contentSlideDuration={300}
      onClick={closeModalWithRedirect}
      fullScreen={isPhone}
      disableBodyScroll={!isPhone}
    >
      <Modal id="tr_welcome_modal">
        <Container>
          <Body>
            <Title>{i18n.t('builder.panel.topresume_welcome_modal.title')}</Title>

            <ItemsTitle>{i18n.t('builder.panel.topresume_welcome_modal.items_title')}</ItemsTitle>

            <Items>
              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_one.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_one.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_two.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_two.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_three.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_three.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_four.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_four.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_five.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_five.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_six.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_six.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_seven.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_seven.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_eight.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_eight.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>

              <ItemContainer>
                <TickContainer>
                  <Tick />
                </TickContainer>
                <ItemDescriptionContainer>
                  <ItemTitle>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_nine.title')}
                  </ItemTitle>
                  <ItemDescription>
                    {i18n.t('builder.panel.topresume_welcome_modal.item_nine.description')}
                  </ItemDescription>
                </ItemDescriptionContainer>
              </ItemContainer>
            </Items>
          </Body>
          <Footer>
            {showCase0 && (
              <CTA
                onClick={() => {
                  trackInternalEvent('click_cta_new_welcome_modal', {
                    cta: 'get_started',
                  })
                  closeModal()
                }}
              >
                {i18n.t('builder.panel.topresume_welcome_modal.get_started')}
              </CTA>
            )}

            {showCase1 && (
              <Fragment>
                <CTA
                  size="small"
                  theme={ButtonTheme.ghost}
                  onClick={() => {
                    trackInternalEvent('click_cta_new_welcome_modal', {
                      cta: 'activate_resume_distribution',
                    })
                    closeModal()
                    navigate(`/resume-distribution`)
                  }}
                >
                  {i18n.t('builder.panel.topresume_welcome_modal.activate_resume_distribution')}
                </CTA>

                <CTA
                  size="small"
                  onClick={() => {
                    trackInternalEvent('click_cta_new_welcome_modal', { cta: 'resume_by_expert' })
                    closeModal()
                    navigate(`/tr-documents/${resumeByExpert?.id}`)
                  }}
                >
                  {i18n.t('builder.panel.topresume_welcome_modal.resume_by_expert')}
                </CTA>
              </Fragment>
            )}

            {showCase2 && (
              <CTA
                onClick={() => {
                  trackInternalEvent('click_cta_new_welcome_modal', { cta: 'resume_by_expert' })
                  closeModal()
                  navigate(`/tr-documents/${resumeByExpert?.id}`)
                }}
              >
                {i18n.t('builder.panel.topresume_welcome_modal.resume_by_expert')}
              </CTA>
            )}

            {showCase3 && (
              <CTA
                onClick={() => {
                  trackInternalEvent('click_cta_new_welcome_modal', { cta: 'view_resume' })
                  closeModal()
                  navigate(refreshedResume ? `/resumes/${refreshedResume.id}/edit` : '/resumes')
                }}
              >
                {i18n.t('builder.panel.topresume_welcome_modal.view_resume')}
              </CTA>
            )}

            {showCase4 && (
              <CTA
                onClick={() => {
                  trackInternalEvent('click_cta_new_welcome_modal', {
                    cta: 'explore_recommended_jobs',
                  })
                  closeModal()
                }}
              >
                {i18n.t('builder.panel.topresume_welcome_modal.explore_recommended_jobs')}
              </CTA>
            )}
          </Footer>
        </Container>
        <CloseButton onClick={closeModalWithRedirect} data-testid="tr-welcome-modal-close-button" />
      </Modal>
    </ModalOverlay>
  )
}
